import AV from '../config/leancloud';
import { activityLogService } from './activityLogService';

export const PAYMENT_TYPES = {
  TUITION: 'tuition',
  REFUND: 'refund',
  EXPENSE: 'expense'
};

export const PAYMENT_METHODS = {
  WECHAT: 'wechat',
  ALIPAY: 'alipay',
  CASH: 'cash',
  OTHER: 'other'
};

export const paymentService = {
  async createPayment(data) {
    try {
      const Payment = AV.Object.extend('Payment');
      const payment = new Payment();

      // Handle file upload if receipt exists
      if (data.receipt instanceof File) {
        const receiptFile = new AV.File(data.receipt.name, data.receipt);
        const savedFile = await receiptFile.save();
        payment.set('receipt', savedFile);
      }

      // Set other payment fields
      const paymentData = {
        amount: data.amount,
        type: data.type,
        method: data.method || PAYMENT_METHODS.CASH,
        date: data.date || new Date(),
        description: data.description,
        createdBy: AV.User.current(),
        student: data.student // Pointer to Student
      };

      // Set ACL
      const acl = new AV.ACL();
      acl.setPublicReadAccess(true);      // Everyone can read
      acl.setWriteAccess(AV.User.current().id, true);  // Only creator can write
      payment.setACL(acl);

      Object.entries(paymentData).forEach(([key, value]) => {
        if (value !== undefined) {
          payment.set(key, value);
        }
      });

      const savedPayment = await payment.save();

      // Log the payment activity
      const studentName = data.student ? await data.student.get('name') : '未知学员';
      const paymentTypeText = {
        [PAYMENT_TYPES.TUITION]: '学费',
        [PAYMENT_TYPES.REFUND]: '退费',
        [PAYMENT_TYPES.EXPENSE]: '支出'
      }[data.type] || data.type;

      const paymentMethodText = {
        [PAYMENT_METHODS.WECHAT]: '微信',
        [PAYMENT_METHODS.ALIPAY]: '支付宝',
        [PAYMENT_METHODS.CASH]: '现金',
        [PAYMENT_METHODS.OTHER]: '其他'
      }[data.method || PAYMENT_METHODS.CASH];

      await activityLogService.logActivity(
        '创建支付记录',
        `${paymentTypeText} - ${studentName}: ¥${data.amount} (${paymentMethodText})${data.description ? ` - ${data.description}` : ''}`
      );

      return savedPayment;
    } catch (error) {
      console.error('Create payment error:', error);
      throw error;
    }
  },

  async getPayments({ type, startDate, endDate } = {}) {
    try {
      const query = new AV.Query('Payment');
      
      if (type) {
        query.equalTo('type', type);
      }

      if (startDate) {
        query.greaterThanOrEqualTo('date', startDate);
      }

      if (endDate) {
        query.lessThanOrEqualTo('date', endDate);
      }

      query.descending('date');
      query.include('createdBy'); // Include user info
      query.include('student');   // Include student info
      
      const payments = await query.find();

      // Log the query activity if it's a filtered search
      if (type || startDate || endDate) {
        await activityLogService.logActivity(
          '查询支付记录',
          `查询${type ? ` ${type}类型` : ''}支付记录${startDate ? ` 从 ${startDate.toLocaleDateString()}` : ''}${endDate ? ` 到 ${endDate.toLocaleDateString()}` : ''}`
        );
      }

      return payments.map(payment => ({
        objectId: payment.id,
        amount: payment.get('amount'),
        type: payment.get('type'),
        method: payment.get('method'),
        date: payment.get('date'),
        description: payment.get('description'),
        receipt: payment.get('receipt')?.url(),
        createdBy: payment.get('createdBy')?.get('username'),
        student: payment.get('student')?.get('name'),
        createdAt: payment.createdAt
      }));
    } catch (error) {
      console.error('Get payments error:', error);
      throw error;
    }
  },

  async updatePayment(paymentId, data) {
    try {
      const Payment = AV.Object.extend('Payment');
      const payment = AV.Object.createWithoutData('Payment', paymentId);
      
      // Handle file upload if new receipt exists
      if (data.receipt instanceof File) {
        const receiptFile = new AV.File(data.receipt.name, data.receipt);
        const savedFile = await receiptFile.save();
        payment.set('receipt', savedFile);
      }

      // Set other payment fields
      const paymentData = {
        amount: data.amount,
        method: data.method,
        date: data.date,
        description: data.description
      };

      Object.entries(paymentData).forEach(([key, value]) => {
        if (value !== undefined) {
          payment.set(key, value);
        }
      });

      const updatedPayment = await payment.save();

      // Log the payment update activity
      const paymentTypeText = {
        [PAYMENT_TYPES.TUITION]: '学费',
        [PAYMENT_TYPES.REFUND]: '退费',
        [PAYMENT_TYPES.EXPENSE]: '支出'
      }[data.type] || data.type;

      const paymentMethodText = {
        [PAYMENT_METHODS.WECHAT]: '微信',
        [PAYMENT_METHODS.ALIPAY]: '支付宝',
        [PAYMENT_METHODS.CASH]: '现金',
        [PAYMENT_METHODS.OTHER]: '其他'
      }[data.method];

      await activityLogService.logActivity(
        '更新支付记录',
        `更新${paymentTypeText}记录: ¥${data.amount} (${paymentMethodText})${data.description ? ` - ${data.description}` : ''}`
      );

      return updatedPayment;
    } catch (error) {
      console.error('Update payment error:', error);
      throw error;
    }
  }
}; 